import React, { useEffect, useMemo, useState } from "react";
import { Grid3x3, Filter, ChevronDown, Loader2, Layers } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import ParamInfo from "./ParamInfo";
import { SEV_MILD, SEV_CONCERN } from "../lib/severityTiers";

// Cell colour + label per lab status. Keeps the grid a single-hue scan.
const STATUS_STYLE = {
  low:  { bg: "#F59E0B", text: "#0F172A", label: "LOW",  weight: 1 },
  high: { bg: "#EF4444", text: "#FFFFFF", label: "HIGH", weight: 0.5 },
  ok:   { bg: "#065F46", text: "#D1FAE5", label: "OK",   weight: 0 },
  na:   { bg: "#1E293B", text: "#64748B", label: "—",    weight: 0 },
};
// Severity chip class — tier thresholds imported from ../lib/severityTiers
// so the grid stays in sync with the trend chart and drill-down modal.
function sevChipStyle(score) {
  if (!score || score < SEV_MILD) return { className: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40" };
  if (score < SEV_CONCERN)         return { className: "bg-amber-500/15 text-amber-200 border-amber-500/40" };
  return { className: "bg-rose-500/15 text-rose-200 border-rose-500/40" };
}

/**
 * Squad Health Weather Map — one-screen triage grid.
 *
 * Rows are athletes, columns are the top-N most-tested markers. Each cell
 * carries the latest lab reading colour-coded by status. Athletes are sorted
 * "worst-first" (server-side ranking by low + 0.5*high). Optional class
 * filter narrows to a single sailing class.
 */
export default function SquadWeatherMap({ parameters }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [cls, setCls] = useState("");
  const [squad, setSquad] = useState("");
  // "Show all markers" toggle — ON by default so both the Athlete
  // and Marker axis views land on the full audit view; coaches can
  // switch to Top 14 for a quick scan (2026-02-20 default flip).
  const [allMarkers, setAllMarkers] = useState(true);
  // Axis toggle — "athletes" = athletes-as-rows (default), "markers" =
  // markers-as-rows / athletes-as-columns for the "who has low Fe?"
  // sweep (2026-02-20 request).
  const [axis, setAxis] = useState("athletes");
  const paramByKey = useMemo(
    () => Object.fromEntries((parameters || []).map((p) => [p.key, p])),
    [parameters],
  );

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cls) params.set("class_filter", cls);
    if (squad) params.set("squad_filter", squad);
    if (allMarkers) params.set("all_markers", "true");
    const q = params.toString() ? `?${params.toString()}` : "";
    api.get(`/squad/weather-map${q}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [cls, squad, allMarkers]);

  // Unique classes + squads across the loaded set so the filters are dynamic.
  const [allClasses, setAllClasses] = useState([]);
  const [allSquads, setAllSquads] = useState([]);
  useEffect(() => {
    api.get("/sailors").then((r) => {
      const c = new Set();
      const squadCounts = new Map();
      for (const a of r.data || []) {
        if (a.archived) continue;
        const raw = (a.subgroup || "").trim();
        if (raw) {
          raw.split(/[,/&]/).forEach((p) => {
            const cc = p.trim();
            if (cc) c.add(cc);
          });
        }
        if (a.group) squadCounts.set(a.group, (squadCounts.get(a.group) || 0) + 1);
      }
      setAllClasses(Array.from(c).sort());
      setAllSquads(Array.from(squadCounts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])));
    }).catch(() => { setAllClasses([]); setAllSquads([]); });
  }, []);

  return (
    <div className="card p-5 md:p-6" data-testid="squad-weather-map">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Grid3x3 size={12} className="text-cyan-300" /> Squad Health Weather Map
          </div>
          <div className="font-display text-lg text-slate-100 mt-0.5">
            Latest labs · at a glance · worst first
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Each cell is the latest bloodwork status. Sorted by weighted clinical severity
            (importance × direction × magnitude) so critical deviations rank above high-count mild ones.
          </div>
          {data && (
            <div className="text-[11px] text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="weather-map-counts">
              <span>
                <span className="text-cyan-200 font-mono-data">{data.athletes.length}</span>
                <span className="text-slate-500"> shown</span>
              </span>
              {data.no_lab_count > 0 && (
                <span title="Active athletes who don't have any lab record yet — add a lab in Data Editor to include them">
                  <span className="text-amber-300 font-mono-data">{data.no_lab_count}</span>
                  <span className="text-slate-500"> without labs</span>
                </span>
              )}
              {data.exempt_count > 0 && (
                <span title="Coaches / staff / long-term absentees excluded from the diagnostic program">
                  <span className="text-slate-300 font-mono-data">{data.exempt_count}</span>
                  <span className="text-slate-500"> exempt</span>
                </span>
              )}
              <span className="text-slate-500">
                (of <span className="text-slate-300 font-mono-data">{data.active_count + data.exempt_count}</span> active)
              </span>
            </div>
          )}
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
          <Filter size={11} />
          <div className="relative">
            <select
              value={cls}
              onChange={(e) => setCls(e.target.value)}
              className="form-input !py-1.5 !pl-2 !pr-7 !text-[11px] appearance-none"
              data-testid="weather-map-class-filter"
            >
              <option value="">All classes</option>
              {allClasses.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500" />
          </div>
        </label>
        {/* Marker-count toggle — segmented so both options are visible at once. */}
        <div className="inline-flex rounded-lg border border-[#1E304B] overflow-hidden">
          <button
            onClick={() => setAllMarkers(false)}
            className={`text-[10px] uppercase tracking-widest px-2.5 py-1.5 ${
              !allMarkers
                ? "bg-cyan-500/25 text-cyan-100"
                : "text-slate-400 hover:bg-[#122036]"
            }`}
            data-testid="weather-map-top14"
            title="Top 14 most-covered markers — daily scan view"
          >
            Top 14
          </button>
          <button
            onClick={() => setAllMarkers(true)}
            className={`text-[10px] uppercase tracking-widest px-2.5 py-1.5 border-l border-[#1E304B] ${
              allMarkers
                ? "bg-cyan-500/25 text-cyan-100"
                : "text-slate-400 hover:bg-[#122036]"
            }`}
            data-testid="weather-map-all-markers"
            title="Every marker tested on the squad — full audit view"
          >
            All {data?.total_markers_tested ?? ""}
          </button>
        </div>
        {/* Axis toggle — swap between athlete-rows (default) and
            marker-rows (transposed) so coaches can scan either way. */}
        <div className="inline-flex rounded-lg border border-[#1E304B] overflow-hidden">
          <button
            onClick={() => setAxis("athletes")}
            className={`text-[10px] uppercase tracking-widest px-2.5 py-1.5 ${
              axis === "athletes"
                ? "bg-emerald-500/25 text-emerald-100"
                : "text-slate-400 hover:bg-[#122036]"
            }`}
            data-testid="weather-map-axis-athletes"
            title="Athletes as rows, markers as columns (default)"
          >
            Athletes ↓
          </button>
          <button
            onClick={() => setAxis("markers")}
            className={`text-[10px] uppercase tracking-widest px-2.5 py-1.5 border-l border-[#1E304B] ${
              axis === "markers"
                ? "bg-emerald-500/25 text-emerald-100"
                : "text-slate-400 hover:bg-[#122036]"
            }`}
            data-testid="weather-map-axis-markers"
            title="Markers as rows, athletes as columns — for one-marker sweeps"
          >
            Markers ↓
          </button>
        </div>
      </div>

      {/* Squad filter pills — coach can restrict the map to one top-
          level squad (Current Athletes / MJPT / etc). Hidden entirely
          if only one squad exists so the toolbar stays uncluttered. */}
      {allSquads.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Layers size={10} /> Squad
          </span>
          <button
            onClick={() => setSquad("")}
            className={`text-[10px] px-1.5 py-0.5 rounded-full border font-mono-data transition ${
              squad === ""
                ? "bg-cyan-500/25 border-cyan-400/60 text-cyan-100"
                : "bg-[#0E1A2E] border-[#1E304B] text-slate-300 hover:border-cyan-500/40"
            }`}
            data-testid="weather-map-squad-all"
          >
            All
          </button>
          {allSquads.map(([name, n]) => {
            const active = squad === name;
            return (
              <button
                key={name}
                onClick={() => setSquad(active ? "" : name)}
                className={`text-[10px] px-1.5 py-0.5 rounded-full border font-mono-data transition ${
                  active
                    ? "bg-cyan-500/25 border-cyan-400/60 text-cyan-100"
                    : "bg-[#0E1A2E] border-[#1E304B] text-slate-300 hover:border-cyan-500/40"
                }`}
                title={`${n} athlete${n === 1 ? "" : "s"}`}
                data-testid={`weather-map-squad-${name.replace(/\W+/g, "-")}`}
              >
                {name} <span className="opacity-60">· {n}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-slate-500 text-sm">
          <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
        </div>
      ) : !data || !data.athletes.length ? (
        <div className="rounded-lg border border-dashed border-slate-700 p-6 text-center text-sm text-slate-400">
          No athletes with lab data{cls && ` in class ${cls}`}.
        </div>
      ) : (() => {
        // Shrink every column when the coach opens ALL markers so the
        // grid stays scan-friendly. `dense` swaps padding, font size,
        // and forces a narrow explicit width so 60+ columns fit
        // without horizontal scroll on a typical laptop.
        const dense = allMarkers;
        const cellPad = dense ? "px-0.5 py-1" : "px-1 py-1.5";
        const headPad = dense ? "px-0.5 py-1.5" : "px-1.5 py-2";
        const valTxt = dense ? "text-[9px]" : "text-[10px]";
        const headTxt = dense ? "text-[8px]" : "text-[9px]";
        const cellWidth = dense ? "min-w-[28px] max-w-[36px]" : "";
        if (axis === "markers") {
          return (
            <MarkerRowsTable
              data={data}
              paramByKey={paramByKey}
              cellPad={cellPad}
              headPad={headPad}
              valTxt={valTxt}
              headTxt={headTxt}
              cellWidth={cellWidth}
              dense={dense}
            />
          );
        }
        return (
        <div className="overflow-auto" style={{ maxHeight: 620 }}>
          <table className={`w-full ${dense ? "text-[10px]" : "text-[11px]"}`}>
            <thead className="sticky top-0 bg-[#0B1627] z-10">
              <tr>
                <th className="text-left px-2 py-2 text-slate-400 font-normal uppercase tracking-wider text-[9px] sticky left-0 bg-[#0B1627] z-20 min-w-[180px]">
                  Athlete
                </th>
                <th className="text-right px-2 py-2 text-slate-400 font-normal uppercase tracking-wider text-[9px]" title="Age in whole years from baseline DOB">
                  Age
                </th>
                <th className="text-right px-2 py-2 text-slate-400 font-normal uppercase tracking-wider text-[9px]">
                  Last
                </th>
                <th className="text-right px-2 py-2 text-slate-400 font-normal uppercase tracking-wider text-[9px]" title="Days since the athlete's last lab">
                  Ago
                </th>
                <th className="text-center px-2 py-2 text-slate-400 font-normal uppercase tracking-wider text-[9px]">
                  Flags
                </th>
                <th className="text-center px-2 py-2 text-slate-400 font-normal uppercase tracking-wider text-[9px]" title="Weighted clinical severity — importance × direction × magnitude. Higher = more concerning.">
                  Severity
                </th>
                {data.markers.map((k) => {
                  const p = paramByKey[k];
                  return (
                    <th key={k} className={`${headPad} ${cellWidth} text-center text-slate-400 font-normal uppercase tracking-wider ${headTxt}`}>
                      <div className="flex items-center justify-center gap-0.5 truncate">
                        {p?.label || k.toUpperCase()}
                        {p && <ParamInfo paramKey={k} low={p.range_low} high={p.range_high} />}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.athletes.map((a) => (
                <tr key={a.id} className="border-t border-[#1E304B] hover:bg-slate-900/40" data-testid={`wm-row-${a.id}`}>
                  <td className="px-2 py-1.5 text-slate-200 sticky left-0 bg-[#0B1627] hover:bg-slate-900/40 z-10">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">{a.subgroup || a.group}</div>
                  </td>
                  <td className="px-2 py-1.5 text-right text-slate-300 font-mono-data text-[10px]">
                    {a.age_years != null ? a.age_years : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right text-slate-500 font-mono-data text-[10px] whitespace-nowrap">
                    {fmtDate(a.last_lab_date)}
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right font-mono-data text-[10px] whitespace-nowrap ${
                      a.days_since_lab == null
                        ? "text-slate-500"
                        : a.days_since_lab > 180
                          ? "text-rose-300"
                          : a.days_since_lab > 90
                            ? "text-amber-300"
                            : "text-slate-300"
                    }`}
                    title={a.days_since_lab == null ? "" : `${a.days_since_lab} days since last lab`}
                  >
                    {a.days_since_lab == null ? "—" : `${a.days_since_lab}d`}
                  </td>
                  <td className="px-2 py-1.5 text-center whitespace-nowrap">
                    {a.low_count > 0 && (
                      <span className="inline-block px-1 rounded bg-amber-500/20 text-amber-200 font-mono-data text-[10px] mr-0.5" title={`${a.low_count} low`}>
                        {a.low_count}L
                      </span>
                    )}
                    {a.high_count > 0 && (
                      <span className="inline-block px-1 rounded bg-rose-500/20 text-rose-200 font-mono-data text-[10px]" title={`${a.high_count} high`}>
                        {a.high_count}H
                      </span>
                    )}
                    {a.low_count === 0 && a.high_count === 0 && (
                      <span className="text-emerald-300 font-mono-data text-[10px]">clear</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center whitespace-nowrap">
                    {a.severity_score > 0 ? (
                      <span
                        className={`inline-block px-1.5 py-0.5 rounded border font-mono-data text-[10px] ${sevChipStyle(a.severity_score).className}`}
                        title="Weighted clinical-severity score across latest OOR markers."
                        data-testid={`wm-sev-${a.id}`}
                      >
                        {a.severity_score.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-emerald-300 font-mono-data text-[10px]">0</span>
                    )}
                  </td>
                  {data.markers.map((k) => {
                    const cell = a.markers[k];
                    const style = STATUS_STYLE[cell?.status || "na"];
                    return (
                      <td
                        key={k}
                        className={`${cellPad} ${cellWidth} text-center`}
                        style={{
                          background: style.bg,
                          color: style.text,
                        }}
                        title={cell?.status !== "na" ? `${k.toUpperCase()} ${cell?.value} · ${style.label}${cell?.severity ? ` · sev ${cell.severity}` : ""}` : "No data"}
                        data-testid={`wm-cell-${a.id}-${k}`}
                      >
                        <div className={`font-mono-data ${valTxt} leading-tight truncate`}>
                          {cell?.value != null ? cell.value : "—"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        );
      })()}
    </div>
  );
}


/**
 * MarkerRowsTable — transposed view of the weather map. Rows are
 * markers, columns are athletes. Handy when a coach wants to sweep
 * a single marker across the squad ("who's low on Fe?") in a single
 * horizontal scan. Uses the SAME dense styling knobs as the athlete
 * view for consistency.
 */
function MarkerRowsTable({ data, paramByKey, cellPad, headPad, valTxt, headTxt, cellWidth, dense }) {
  // Count status per marker so we can rank markers worst-first — same
  // logic as the athlete axis but pivoted. Undefined cells (marker not
  // measured on that athlete's latest lab) don't count towards flags.
  const markerStats = useMemo(() => {
    const stats = {};
    for (const k of data.markers) {
      let low = 0, high = 0;
      for (const a of data.athletes) {
        const c = a.markers[k];
        if (!c) continue;
        if (c.status === "low") low++;
        else if (c.status === "high") high++;
      }
      stats[k] = { low, high, rank: low + 0.5 * high };
    }
    return stats;
  }, [data]);

  const sortedMarkers = useMemo(() => (
    [...data.markers].sort((a, b) => markerStats[b].rank - markerStats[a].rank)
  ), [data.markers, markerStats]);

  return (
    <div className="overflow-auto" style={{ maxHeight: 620 }}>
      <table className={`w-full ${dense ? "text-[10px]" : "text-[11px]"}`}>
        <thead className="sticky top-0 bg-[#0B1627] z-10">
          <tr>
            <th className={`text-left ${headPad} text-slate-400 font-normal uppercase tracking-wider ${headTxt} sticky left-0 bg-[#0B1627] z-20 min-w-[140px]`}>
              Marker
            </th>
            <th className={`text-center ${headPad} text-slate-400 font-normal uppercase tracking-wider ${headTxt} sticky left-[140px] bg-[#0B1627] z-20`}
                title="Number of athletes with LOW / HIGH values on this marker">
              Flags
            </th>
            {data.athletes.map((a) => (
              <th key={a.id}
                  className={`${headPad} ${cellWidth} text-center text-slate-100 font-bold uppercase tracking-wider ${headTxt}`}
                  title={`${a.name}${a.subgroup ? ` · ${a.subgroup}` : ""}${a.age_years != null ? ` · age ${a.age_years}` : ""}`}>
                <div className="truncate max-w-[80px] font-bold" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", height: 90 }}>
                  {a.name}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedMarkers.map((k) => {
            const p = paramByKey[k];
            const s = markerStats[k];
            return (
              <tr key={k} className="border-t border-[#1E304B] hover:bg-slate-900/40" data-testid={`wm-mrow-${k}`}>
                <td className={`${cellPad} text-slate-200 sticky left-0 bg-[#0B1627] z-10`}>
                  <div className="flex items-center gap-1 font-medium">
                    {p?.label || k.toUpperCase()}
                    {p && <ParamInfo paramKey={k} low={p.range_low} high={p.range_high} />}
                  </div>
                  {p?.unit && (
                    <div className="text-[9px] text-slate-500 font-mono-data">{p.unit}</div>
                  )}
                </td>
                <td className={`${cellPad} text-center whitespace-nowrap sticky left-[140px] bg-[#0B1627] z-10`}>
                  {s.low > 0 && (
                    <span className="inline-block px-1 rounded bg-amber-500/20 text-amber-200 font-mono-data text-[10px] mr-0.5" title={`${s.low} low`}>
                      {s.low}L
                    </span>
                  )}
                  {s.high > 0 && (
                    <span className="inline-block px-1 rounded bg-rose-500/20 text-rose-200 font-mono-data text-[10px]" title={`${s.high} high`}>
                      {s.high}H
                    </span>
                  )}
                  {s.low === 0 && s.high === 0 && (
                    <span className="text-emerald-300 font-mono-data text-[10px]">clear</span>
                  )}
                </td>
                {data.athletes.map((a) => {
                  const cell = a.markers[k];
                  const style = STATUS_STYLE[cell?.status || "na"];
                  return (
                    <td
                      key={a.id}
                      className={`${cellPad} ${cellWidth} text-center`}
                      style={{ background: style.bg, color: style.text }}
                      title={cell?.status !== "na" ? `${a.name} · ${k.toUpperCase()} ${cell?.value} · ${style.label}${cell?.severity ? ` · sev ${cell.severity}` : ""}` : `${a.name} — no data`}
                      data-testid={`wm-mcell-${k}-${a.id}`}
                    >
                      <div className={`font-mono-data ${valTxt} leading-tight truncate`}>
                        {cell?.value != null ? cell.value : "—"}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

